import React, { FC, useState } from 'react';
import { Upload } from 'antd';
import { RcFile } from 'antd/lib/upload/interface';
import { useTranslation } from 'react-i18next';
import { InboxOutlined, FileExcelOutlined, DeleteOutlined } from '@ant-design/icons';

import colors from '~/constants/colors';
import humanFileSize from '~/utils/humanFileSize';
import {
    Container,
    UploadArea,
    UploadText,
    UploadHint,
    FileDisplay,
    FileInfo,
    FileName,
    DeleteButton,
    ErrorMessage,
} from './LeadsUploadInput.style';

interface LeadsUploadInputProps {
    onChange?: (file: File | undefined) => void;
    value?: File;
    disabled?: boolean;
    accept?: string;
    maxSizeB?: number;
}

const LeadsUploadInput: FC<LeadsUploadInputProps> = ({
    onChange,
    value,
    disabled = false,
    accept = '.csv',
    maxSizeB = 10 * 1024 * 1024, // 10MB default
}) => {
    const { t } = useTranslation('sales');
    const [errorMessage, setErrorMessage] = useState('');

    // Truncate filename if longer than maxLength
    const truncateFileName = (fileName: string, maxLength: number = 35): string => {
        if (fileName.length <= maxLength) {
            return fileName;
        }

        // Get file extension
        const lastDotIndex = fileName.lastIndexOf('.');
        const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
        const nameWithoutExt = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;

        // Calculate how many characters we can show from the name
        const availableLength = maxLength - extension.length - 3; // 3 for "..."

        if (availableLength <= 0) {
            return `...${extension}`;
        }

        return `${nameWithoutExt.substring(0, availableLength)}...${extension}`;
    };

    const beforeUpload = (rcFile: RcFile) => {
        setErrorMessage('');

        // Validate file type - CSV only
        const isCsv =
            rcFile.type === 'text/csv' ||
            rcFile.type === 'application/csv' ||
            rcFile.name.endsWith('.csv');

        if (!isCsv) {
            setErrorMessage(t('You can only upload CSV files (.csv)'));
            return false;
        }

        // Validate file size
        if (rcFile.size > maxSizeB) {
            setErrorMessage(
                `${t('File is too large')}. ${humanFileSize(rcFile.size)} / ${humanFileSize(maxSizeB)} ${t('allowed')}`
            );
            return false;
        }

        // Pass file to parent without uploading
        onChange?.(rcFile as File);
        return false; // Prevent default upload behavior
    };

    const handleRemove = () => {
        onChange?.(undefined);
        setErrorMessage('');
    };

    return (
        <Container>
            {!value ? (
                <Upload.Dragger
                    accept={accept}
                    beforeUpload={beforeUpload}
                    fileList={[]}
                    disabled={disabled}
                    showUploadList={false}
                >
                    <UploadArea>
                        <InboxOutlined style={{ fontSize: 48, color: colors.blue[500] }} />
                        <UploadText>{t('Click or drag file to this area to upload')}</UploadText>
                        <UploadHint>{t('Support for CSV files only (.csv)')}</UploadHint>
                        <UploadHint>
                            {t('Maximum file size')}: {humanFileSize(maxSizeB, true, 0)}
                        </UploadHint>
                    </UploadArea>
                </Upload.Dragger>
            ) : (
                <FileDisplay>
                    <FileInfo>
                        <FileExcelOutlined style={{ fontSize: 32, color: colors.blue[600] }} />
                        <FileName title={value.name}>{truncateFileName(value.name)}</FileName>
                    </FileInfo>
                    <DeleteButton onClick={handleRemove} disabled={disabled}>
                        <DeleteOutlined />
                    </DeleteButton>
                </FileDisplay>
            )}

            {errorMessage && <ErrorMessage role="alert">{errorMessage}</ErrorMessage>}
        </Container>
    );
};

export default LeadsUploadInput;
